import {
  useLazyGetChatHistoryQuery,
  usePatchChatSessionMutation,
  usePostNewChatMutation,
} from "@api/chat";
import { receiveStream } from "@api/chatStreaming";
import { useGetCourseQuery } from "@api/course";
import Header from "@components/Header";
import { ArrowDownIcon } from "@heroicons/react/16/solid";
import { useToastEffect } from "@hooks/hooks";
import type { ChatMessageDTO } from "@onetutor/shared/dto";
import { MessageEnum } from "@onetutor/shared/type";
import { useRequiredParams } from "@utils/params";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { ChatBubble } from "./components/ChatBubble";
import { ChatInputBar } from "./components/ChatInputBar";
import { ChatSessionDrawer } from "./components/ChatSessionDrawer";
import { TypewriterWelcome } from "./components/Typewriter";

const ChatPage: React.FC = () => {
  const { courseId } = useRequiredParams(["courseId"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("sessionId");

  const [messages, setMessages] = useState<ChatMessageDTO[]>([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null,
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const [isWaitingForFirstToken, setIsWaitingForFirstToken] = useState(false);
  const skipFetchForSessionIdRef = useRef<string | null>(null);

  const { t } = useTranslation("chatPage");

  const { data: course } = useGetCourseQuery(courseId);

  const breadcrumbs = useMemo(
    () => [
      {
        name: course?.name ?? t("breadcrumbFallback.course", { ns: "header" }),
        link: `/course/${courseId}`,
      },
      { name: t("chat") },
    ],
    [course?.name, courseId, t],
  );

  const [
    triggerGetHistory,
    {
      data: chatHistory,
      isFetching: isFetchingChatHistory,
      isError: chatHistoryIsError,
      error: chatHistoryError,
    },
  ] = useLazyGetChatHistoryQuery();

  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    if (skipFetchForSessionIdRef.current === sessionId) {
      skipFetchForSessionIdRef.current = null;
      return;
    }
    triggerGetHistory({ courseId, sessionId });
  }, [courseId, sessionId, triggerGetHistory]);

  useToastEffect({ isError: chatHistoryIsError, error: chatHistoryError });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setShowScrollButton(false);
  }, []);

  useEffect(() => {
    const history = chatHistory?.chatMessages;
    if (history) {
      setMessages(history);
      setTimeout(scrollToBottom, 0);
    }
  }, [chatHistory?.chatMessages, scrollToBottom]);

  useLayoutEffect(() => {
    if (messages.length === 0) {
      return;
    }
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const el = messagesEndRef.current;
    if (!el) {
      return;
    }
    const obs = new IntersectionObserver(
      ([e]) => setShowScrollButton(!e.isIntersecting),
      { threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const [createChat] = usePostNewChatMutation();
  const [patchChatSession] = usePatchChatSessionMutation();

  const handleSubmit = useCallback(
    async (input: string) => {
      if (isStreaming || !input.trim()) {
        return;
      }
      const userId = uuidv4();
      const aiId = uuidv4();
      setMessages((prev) => [
        ...prev,
        {
          id: userId,
          content: input,
          author: MessageEnum.HUMAN,
          citations: null,
        },
        { id: aiId, content: "", author: MessageEnum.AI, citations: null },
      ]);
      scrollToBottom();

      setStreamingMessageId(aiId);
      setIsStreaming(true);
      setIsWaitingForFirstToken(true);

      let sid = sessionId;
      if (!sid) {
        const res = await createChat({ courseId });
        sid = res.data?.id ?? null;
        if (!sid) {
          return;
        }
        patchChatSession({
          sessionId: sid,
          courseId,
          body: { name: input.slice(0, 50) },
        });
        skipFetchForSessionIdRef.current = sid;
        setSearchParams({ sessionId: sid }, { replace: true });
      }

      receiveStream({
        courseId,
        sessionId: sid,
        streamingMessageId: aiId,
        setIsStreaming,
        setMessages,
        setIsWaitingForFirstToken,
        userInput: input,
      });
    },
    [
      isStreaming,
      sessionId,
      courseId,
      createChat,
      patchChatSession,
      setSearchParams,
      scrollToBottom,
    ],
  );

  return (
    <Header scrollable={false} bottomPadding={false} breadcrumbs={breadcrumbs}>
      <ChatSessionDrawer>
        <div className="mb-2 flex h-full min-h-0 flex-col items-center justify-center">
          <div className="flex h-full w-full max-w-5xl flex-col">
            <div className="mb-2 flex-1 grow overflow-y-auto rounded-box">
              {messages.map((message) => (
                <ChatBubble
                  key={message.id}
                  message={message}
                  messageArray={messages}
                  citations={message.citations ?? []}
                  isStreaming={isStreaming && message.id === streamingMessageId}
                  isWaitingForFirstToken={
                    isWaitingForFirstToken && message.id === streamingMessageId
                  }
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
            {messages.length === 0 && !isWaitingForFirstToken && (
              <TypewriterWelcome />
            )}
            <div className="relative">
              {showScrollButton && (
                <button
                  type="button"
                  onClick={scrollToBottom}
                  className="btn btn-circle -top-20 absolute inset-x-0 mx-auto bg-primary-neutral opacity-75"
                >
                  <ArrowDownIcon className="h-6 w-6" />
                </button>
              )}
              <ChatInputBar
                handleSubmit={handleSubmit}
                isStreaming={isStreaming}
                isFetchingChatHistory={isFetchingChatHistory}
                newChatPressed={() => {
                  setSearchParams({}, { replace: true });
                }}
                messages={messages}
              />
            </div>
            <div className="mt-sm hidden justify-center whitespace-pre-line text-center text-xs opacity-50 sm:block sm:text-sm">
              {t("disclaimer")}
            </div>
          </div>
        </div>
      </ChatSessionDrawer>
    </Header>
  );
};

export default ChatPage;
